/**
 * Iter 76 — Master Lists (with Semantics) admin page.
 *
 * Super_admin only. Replaces the legacy plain-string MasterListsEditor for
 * the master keys that have semantic schemas (attendance_status_codes,
 * leave_types, employee_designations, departments, document_types,
 * vendor_categories, enquiry_stages).
 *
 * Each entry gets a row of fields driven by the schema fetched from
 * /api/masters/{key}/schema.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Layers, Save, Plus, Trash2, Loader2, AlertCircle, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPut } from '../../lib/api';

function FieldEditor({ field, value, onChange }) {
  if (field.type === 'bool') {
    return (
      <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)}
        className="w-3.5 h-3.5" data-testid={`f-${field.id}`} />
    );
  }
  if (field.type === 'number') {
    return (
      <input type="number" value={value ?? ''} step="0.1"
        onChange={(e) => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
        className="w-full px-2 py-1 border border-slate-200 rounded-sm text-xs"
        data-testid={`f-${field.id}`} />
    );
  }
  if (field.type === 'select') {
    return (
      <select value={value ?? ''} onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1 border border-slate-200 rounded-sm text-xs"
        data-testid={`f-${field.id}`}>
        {(field.options || []).map(o => <option key={o} value={o}>{o || '— none —'}</option>)}
      </select>
    );
  }
  return (
    <input type={field.type === 'email' ? 'email' : 'text'} value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1 border border-slate-200 rounded-sm text-xs"
      data-testid={`f-${field.id}`} />
  );
}

export default function MasterListsRich() {
  const [schemas, setSchemas] = useState({});
  const [selectedKey, setSelectedKey] = useState(null);
  const [schema, setSchema] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [entryCounts, setEntryCounts] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const r = await apiGet('/masters/schemas');
        setSchemas(r.schemas || {});
        if (!selectedKey) {
          setSelectedKey(Object.keys(r.schemas)[0]);
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load master schemas');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch entry counts for all masters once schemas load
  useEffect(() => {
    const keys = Object.keys(schemas);
    if (!keys.length) return;
    let cancelled = false;
    (async () => {
      const counts = {};
      await Promise.all(keys.map(async (k) => {
        try {
          const r = await apiGet(`/masters/${k}/rich`);
          counts[k] = (r.entries || []).length;
        } catch { counts[k] = 0; }
      }));
      if (!cancelled) setEntryCounts(counts);
    })();
    return () => { cancelled = true; };
  }, [schemas]);

  const loadEntries = useCallback(async () => {
    if (!selectedKey) return;
    try {
      const r = await apiGet(`/masters/${selectedKey}/rich`);
      setSchema(r.schema);
      setEntries(r.entries || []);
      setEntryCounts((c) => ({ ...c, [selectedKey]: (r.entries || []).length }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load entries');
    }
  }, [selectedKey]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const updateEntry = (idx, fieldId, val) => {
    setEntries(prev => prev.map((e, i) => i === idx ? { ...e, [fieldId]: val } : e));
  };

  const addEntry = () => {
    const blank = {};
    for (const f of schema.fields) {
      blank[f.id] = f.default !== undefined ? f.default : (f.type === 'bool' ? false : (f.type === 'number' ? 0 : ''));
    }
    setEntries(prev => [...prev, blank]);
  };

  const removeEntry = (idx) => setEntries(prev => prev.filter((_, i) => i !== idx));

  const save = async () => {
    setSaving(true);
    try {
      const r = await apiPut(`/masters/${selectedKey}/rich`, { entries });
      toast.success(`Saved ${r.count} entries for ${schema.label}`);
      setEntries(r.entries);
      setEntryCounts((c) => ({ ...c, [selectedKey]: (r.entries || []).length }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#F8F5EE] min-h-screen" data-testid="masters-rich">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <Link to="/app/settings" className="text-[11px] font-display font-bold uppercase tracking-widest text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-2">
            <ArrowLeft size={12} /> Back to Settings
          </Link>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <Layers size={20} className="text-[#1F7A6E]" /> Master Lists · with Semantics
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Define the BEHAVIOUR of each master entry — not just its label. E.g. a custom attendance code can declare whether it counts as paid, present, or consumes leave balance. Super Admin only.
          </p>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center gap-2 text-slate-500 py-12 justify-center">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-4">
            {/* Sidebar */}
            <aside className="col-span-3 bg-white border border-slate-200 rounded-sm overflow-hidden">
              <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">
                Masters · {Object.keys(schemas).length}
              </div>
              <ul>
                {Object.entries(schemas).map(([k, s]) => (
                  <li key={k}>
                    <button onClick={() => setSelectedKey(k)}
                      className={`w-full text-left px-3 py-2 text-xs border-b border-slate-100 hover:bg-slate-50 ${selectedKey === k ? 'bg-teal-50 border-l-4 border-l-[#1F7A6E]' : ''}`}
                      data-testid={`master-pick-${k}`}
                    >
                      <div className="font-display font-bold">{s.label}</div>
                      <div className="text-[10px] text-slate-500">{entryCounts[k] != null ? `${entryCounts[k]} entries` : '…'} · {s.fields.length} cols</div>
                      {(s.wired_into || []).filter(w => !w.startsWith('(future)')).length > 0 && (
                        <div className="text-[9px] text-emerald-600 mt-0.5 inline-flex items-center gap-1">
                          <Sparkles size={8} /> Wired
                        </div>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </aside>

            {/* Main */}
            <main className="col-span-9 bg-white border border-slate-200 rounded-sm">
              {!schema ? (
                <div className="p-8 text-center text-sm text-slate-500">Select a master to configure.</div>
              ) : (
                <>
                  <div className="px-5 py-4 border-b border-slate-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="font-display font-bold text-lg">{schema.label}</h2>
                        <div className="text-[11px] text-slate-500">{schema.description}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={addEntry}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider border border-[#1F7A6E] text-[#1F7A6E] rounded-sm hover:bg-teal-50"
                          data-testid="add-entry">
                          <Plus size={11} /> Add Entry
                        </button>
                        <button onClick={save} disabled={saving}
                          className="inline-flex items-center gap-1.5 px-4 py-2 text-[11px] font-display font-bold uppercase tracking-wider bg-[#1F7A6E] text-white rounded-sm hover:bg-[#14504E] disabled:opacity-50"
                          data-testid="save-master">
                          {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                          {saving ? 'Saving…' : 'Save'}
                        </button>
                      </div>
                    </div>
                    {schema.wired_into && (
                      <div className="mt-2 text-[10px] text-slate-500 inline-flex items-center gap-1">
                        <Sparkles size={10} className="text-emerald-500" />
                        <strong>Wired into:</strong> {schema.wired_into.join(' · ')}
                      </div>
                    )}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                        <tr>
                          {schema.fields.map(f => (
                            <th key={f.id} className="text-left px-3 py-2 font-display font-bold">
                              {f.label}{f.required && <span className="text-rose-500 ml-0.5">*</span>}
                              {f.help && <div className="text-[9px] text-slate-400 font-normal normal-case">{f.help}</div>}
                            </th>
                          ))}
                          <th className="px-3 py-2"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {entries.length === 0 ? (
                          <tr><td colSpan={schema.fields.length + 1} className="px-4 py-6 text-center text-slate-400 italic">No entries. Click "Add Entry" to start.</td></tr>
                        ) : entries.map((entry, idx) => (
                          <tr key={idx} className="hover:bg-slate-50" data-testid={`entry-row-${idx}`}>
                            {schema.fields.map(f => (
                              <td key={f.id} className="px-3 py-1.5 align-top">
                                <FieldEditor field={f} value={entry[f.id]}
                                  onChange={(v) => updateEntry(idx, f.id, v)} />
                              </td>
                            ))}
                            <td className="px-3 py-1.5 text-right">
                              <button onClick={() => removeEntry(idx)} className="text-slate-400 hover:text-rose-600"
                                data-testid={`remove-${idx}`}>
                                <Trash2 size={12} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-5 py-3 border-t border-slate-200 text-[10px] text-slate-500 flex items-start gap-2">
                    <AlertCircle size={12} className="text-amber-500 mt-0.5 shrink-0" />
                    <div>
                      Entries marked "Wired" actively drive business logic. For "future" labels, the semantics are stored but business code hasn't consumed them yet — adding one wire-up is a 1-line backend change.
                    </div>
                  </div>
                </>
              )}
            </main>
          </div>
        )}
      </div>
    </div>
  );
}
